import { useCallback, useEffect, useState } from 'react'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import * as api from '../db/api.js'
import {
  FIELD_TYPES,
  buildFieldPayload,
  optionsToText,
  typeLabel,
  validateFieldForm,
} from '../lib/leadCustomFields.js'
import '../pages/financeiro/contas.css'

const EMPTY_FORM = { label: '', field_type: 'text', optionsText: '' }

/**
 * Painel para criar, editar e apagar os campos personalizados do lead (#306). Abre em
 * Comercial > Leads. `onChanged` avisa a tela que a lista de campos mudou.
 */
export default function LeadCustomFieldsManager({ open, onClose, onChanged }) {
  const { toast } = useApp()
  const [fields, setFields] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(null)        // null = lista; objeto = criando/editando
  const [editingId, setEditingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await api.getLeadCustomFields()
      setFields(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message || 'Não foi possível carregar os campos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) { setForm(null); load() }
  }, [open, load])

  if (!open) return null
  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }))
  const editing = Boolean(editingId)

  function openCreate() {
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  function openEdit(field) {
    setEditingId(field.id)
    setForm({ label: field.label, field_type: field.field_type, optionsText: optionsToText(field.options) })
  }

  async function handleSave() {
    const problem = validateFieldForm(form, { editing })
    if (problem) { toast(problem, 'error'); return }
    setSaving(true)
    try {
      const payload = buildFieldPayload(form, { editing })
      if (editing) await api.updateLeadCustomField(editingId, payload)
      else await api.createLeadCustomField(payload)
      toast(editing ? 'Campo atualizado.' : 'Campo criado.', 'success')
      setForm(null)
      await load()
      onChanged?.()
    } catch (err) {
      toast(err.message || 'Falha ao salvar o campo.', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(field) {
    if (!window.confirm(`Apagar o campo "${field.label}"? O valor dele some de todos os leads.`)) return
    try {
      await api.deleteLeadCustomField(field.id)
      toast('Campo apagado.', 'success')
      await load()
      onChanged?.()
    } catch (err) {
      toast(err.message || 'Falha ao apagar o campo.', 'error')
    }
  }

  const close = saving ? undefined : onClose

  return (
    <div className="fc-modal-overlay" onMouseDown={close}>
      <div className="fc-modal" style={{ width: 640 }} role="dialog" aria-modal="true" aria-labelledby="campos-lead-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="fc-modal-head" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 id="campos-lead-title">{form ? (editing ? 'Editar campo' : 'Novo campo do lead') : 'Campos do lead'}</h2>
            <div className="fc-desc-sub">
              {form
                ? 'Aparece no cartão do lead e pode ser preenchido pelas automações.'
                : 'Campos próprios para guardar no lead, como cargo, porte da empresa ou cidade.'}
            </div>
          </div>
          <button type="button" className="fc-icon-btn" onClick={onClose} disabled={saving} title="Fechar">
            <Icon n="x" s={15} />
          </button>
        </div>

        {form ? (
          <>
            <div className="fc-modal-body">
              <label className="us-select-field"><span>Nome do campo</span>
                <input
                  className="fc-ctrl"
                  value={form.label}
                  maxLength={120}
                  placeholder="Ex.: Cargo"
                  onChange={(event) => patch({ label: event.target.value })}
                  disabled={saving}
                  autoFocus
                />
              </label>
              <label className="us-select-field"><span>Tipo</span>
                <select
                  className="fc-ctrl"
                  value={form.field_type}
                  onChange={(event) => patch({ field_type: event.target.value })}
                  disabled={saving || editing}
                >
                  {FIELD_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </label>
              {form.field_type === 'select' && (
                <label className="us-select-field"><span>Opções (uma por linha)</span>
                  <textarea
                    className="fc-ctrl"
                    rows={5}
                    value={form.optionsText}
                    onChange={(event) => patch({ optionsText: event.target.value })}
                    disabled={saving}
                  />
                </label>
              )}
              {editing && <div className="fc-desc-sub">A chave e o tipo não mudam depois de criados.</div>}
            </div>
            <div className="fc-modal-foot">
              <button type="button" className="fc-ctrl" onClick={() => setForm(null)} disabled={saving}>Voltar</button>
              <button type="button" className="fc-ctrl primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="fc-modal-body">
              {error && <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
              {loading ? (
                <div className="empty"><div className="empty-desc">Carregando campos...</div></div>
              ) : !fields.length ? (
                <div className="empty">
                  <div className="empty-title">Nenhum campo criado</div>
                  <div className="empty-desc">Crie um campo e use a chave dele na ação "Criar contato do formulário" das automações.</div>
                </div>
              ) : (
                <div className="fc-table-scroll">
                  <table className="fc-table">
                    <thead>
                      <tr><th>Nome</th><th>Chave</th><th>Tipo</th><th>Opções</th><th className="fc-col-acoes">Ações</th></tr>
                    </thead>
                    <tbody>
                      {fields.map((field) => (
                        <tr key={field.id}>
                          <td><div className="fc-desc">{field.label}</div></td>
                          <td><code>{field.key}</code></td>
                          <td>{typeLabel(field.field_type)}</td>
                          <td>{field.field_type === 'select' ? (field.options || []).join(', ') : '-'}</td>
                          <td className="fc-col-acoes">
                            <button type="button" className="fc-icon-btn" title="Editar" aria-label={`Editar ${field.label}`} onClick={() => openEdit(field)}>
                              <Icon n="edit" s={14} />
                            </button>
                            <button type="button" className="fc-icon-btn danger" title="Apagar" aria-label={`Apagar ${field.label}`} onClick={() => handleDelete(field)}>
                              <Icon n="trash" s={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="fc-modal-foot">
              <button type="button" className="fc-ctrl" onClick={onClose}>Fechar</button>
              <button type="button" className="fc-ctrl primary" onClick={openCreate}><Icon n="plus" s={14} /> Novo campo</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
